import type {
  GutBodyConnectionsColorVariant,
  GutBodyConnectionsFlagLevel,
  OrganLookupCode,
} from '@repo/types';
import { AgeGroup } from '@repo/utils';
import {
  type GutBodyConnectionsArtwork,
  pickArtwork,
} from '#assets/images/gutBodyConnections';

import brainHigh from '#assets/images/gutBodyConnections/badges/brain-high.webp';
import brainLow from '#assets/images/gutBodyConnections/badges/brain-low.webp';
import brainModerate from '#assets/images/gutBodyConnections/badges/brain-moderate.webp';
import brainPurple from '#assets/images/gutBodyConnections/badges/brain-purple.webp';
import endocrineHigh from '#assets/images/gutBodyConnections/badges/endocrine-high.webp';
import endocrineLow from '#assets/images/gutBodyConnections/badges/endocrine-low.webp';
import endocrineModerate from '#assets/images/gutBodyConnections/badges/endocrine-moderate.webp';
import endocrinePurple from '#assets/images/gutBodyConnections/badges/endocrine-purple.webp';
import fetusHigh from '#assets/images/gutBodyConnections/badges/fetus-high.webp';
import fetusLow from '#assets/images/gutBodyConnections/badges/fetus-low.webp';
import fetusModerate from '#assets/images/gutBodyConnections/badges/fetus-moderate.webp';
import gutHigh from '#assets/images/gutBodyConnections/badges/gut-high.webp';
import gutLow from '#assets/images/gutBodyConnections/badges/gut-low.webp';
import gutModerate from '#assets/images/gutBodyConnections/badges/gut-moderate.webp';
import gutPurple from '#assets/images/gutBodyConnections/badges/gut-purple.webp';
import heartHigh from '#assets/images/gutBodyConnections/badges/heart-high.webp';
import heartLow from '#assets/images/gutBodyConnections/badges/heart-low.webp';
import heartModerate from '#assets/images/gutBodyConnections/badges/heart-moderate.webp';
import heartPurple from '#assets/images/gutBodyConnections/badges/heart-purple.webp';
import immuneHigh from '#assets/images/gutBodyConnections/badges/immune-high.webp';
import immuneLow from '#assets/images/gutBodyConnections/badges/immune-low.webp';
import immuneModerate from '#assets/images/gutBodyConnections/badges/immune-moderate.webp';
import immunePurple from '#assets/images/gutBodyConnections/badges/immune-purple.webp';
import kidneyHigh from '#assets/images/gutBodyConnections/badges/kidney-high.webp';
import kidneyLow from '#assets/images/gutBodyConnections/badges/kidney-low.webp';
import kidneyModerate from '#assets/images/gutBodyConnections/badges/kidney-moderate.webp';
import kidneyPurple from '#assets/images/gutBodyConnections/badges/kidney-purple.webp';
import liverHigh from '#assets/images/gutBodyConnections/badges/liver-high.webp';
import liverLow from '#assets/images/gutBodyConnections/badges/liver-low.webp';
import liverModerate from '#assets/images/gutBodyConnections/badges/liver-moderate.webp';
import liverPurple from '#assets/images/gutBodyConnections/badges/liver-purple.webp';
import lungHigh from '#assets/images/gutBodyConnections/badges/lung-high.webp';
import lungLow from '#assets/images/gutBodyConnections/badges/lung-low.webp';
import lungModerate from '#assets/images/gutBodyConnections/badges/lung-moderate.webp';
import lungPurple from '#assets/images/gutBodyConnections/badges/lung-purple.webp';
import metabolicHigh from '#assets/images/gutBodyConnections/badges/metabolic-high.webp';
import metabolicLow from '#assets/images/gutBodyConnections/badges/metabolic-low.webp';
import metabolicModerate from '#assets/images/gutBodyConnections/badges/metabolic-moderate.webp';
import metabolicPurple from '#assets/images/gutBodyConnections/badges/metabolic-purple.webp';
import muscleHigh from '#assets/images/gutBodyConnections/badges/muscle-high.webp';
import muscleLow from '#assets/images/gutBodyConnections/badges/muscle-low.webp';
import muscleModerate from '#assets/images/gutBodyConnections/badges/muscle-moderate.webp';
import musclePurple from '#assets/images/gutBodyConnections/badges/muscle-purple.webp';
import reproductiveHigh from '#assets/images/gutBodyConnections/badges/reproductive-high.webp';
import reproductiveLow from '#assets/images/gutBodyConnections/badges/reproductive-low.webp';
import reproductiveModerate from '#assets/images/gutBodyConnections/badges/reproductive-moderate.webp';
import skinHigh from '#assets/images/gutBodyConnections/badges/skin-high.webp';
import skinLow from '#assets/images/gutBodyConnections/badges/skin-low.webp';
import skinModerate from '#assets/images/gutBodyConnections/badges/skin-moderate.webp';
import skinPurple from '#assets/images/gutBodyConnections/badges/skin-purple.webp';

/**
 * The design file's condition icons: a circle per organ and flag level, its
 * background and colour already drawn in. The flag colours are one adult drawing
 * for every kit; purple is drawn on a baby, as only the baby-toddler body uses it.
 * `oral` is missing because the design file never drew it, as on the map.
 */
const BADGE_BY_ORGAN: Partial<
  Record<OrganLookupCode, GutBodyConnectionsArtwork>
> = {
  brain: {
    low: brainLow,
    moderate: brainModerate,
    high: brainHigh,
    purple: brainPurple,
  },
  endocrine: {
    low: endocrineLow,
    moderate: endocrineModerate,
    high: endocrineHigh,
    purple: endocrinePurple,
  },
  gut: {
    low: gutLow,
    moderate: gutModerate,
    high: gutHigh,
    purple: gutPurple,
  },
  heart: {
    low: heartLow,
    moderate: heartModerate,
    high: heartHigh,
    purple: heartPurple,
  },
  immune: {
    low: immuneLow,
    moderate: immuneModerate,
    high: immuneHigh,
    purple: immunePurple,
  },
  kidney: {
    low: kidneyLow,
    moderate: kidneyModerate,
    high: kidneyHigh,
    purple: kidneyPurple,
  },
  liver: {
    low: liverLow,
    moderate: liverModerate,
    high: liverHigh,
    purple: liverPurple,
  },
  lung: {
    low: lungLow,
    moderate: lungModerate,
    high: lungHigh,
    purple: lungPurple,
  },
  metabolic: {
    low: metabolicLow,
    moderate: metabolicModerate,
    high: metabolicHigh,
    purple: metabolicPurple,
  },
  muscle: {
    low: muscleLow,
    moderate: muscleModerate,
    high: muscleHigh,
    purple: musclePurple,
  },
  reproductive: {
    low: reproductiveLow,
    moderate: reproductiveModerate,
    high: reproductiveHigh,
  },
  skin: {
    low: skinLow,
    moderate: skinModerate,
    high: skinHigh,
    purple: skinPurple,
  },
};

/** Pregnant, the reproductive organ is drawn as the fetus, as the map draws it. */
const FETUS: GutBodyConnectionsArtwork = {
  low: fetusLow,
  moderate: fetusModerate,
  high: fetusHigh,
};

export type OrganBadgeQuery = {
  lookupCode: OrganLookupCode;
  flagLevel: GutBodyConnectionsFlagLevel;
  ageGroup: AgeGroup;
  pregnant: boolean;
  /** As on the body map: the variant's own drawing, or a flag-level stand-in. */
  colorVariant?: GutBodyConnectionsColorVariant;
};

/** The organ's icon, or nothing where the design file has no drawing for it. */
export function getOrganBadgeImage({
  lookupCode,
  flagLevel,
  ageGroup,
  pregnant,
  colorVariant,
}: OrganBadgeQuery): string | undefined {
  if (
    lookupCode === 'reproductive' &&
    pregnant &&
    ageGroup === AgeGroup.ADULT
  ) {
    return pickArtwork(FETUS, flagLevel, colorVariant);
  }

  const artwork = BADGE_BY_ORGAN[lookupCode];
  return artwork && pickArtwork(artwork, flagLevel, colorVariant);
}
